import { Button, cardClass, fieldClass, Table } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { isAdmin, useSession } from '../session';
import { Empty, ErrorBanner, ListSkeleton, outcomeMessage, outcomeTone, Time, ToggleGroup, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { formatCell } from '../format';

// Point in time (ADR 0018): a report as the business stood on a date, as it
// was known then or as it is known now, and the months that are closed with
// the changes entered after each close. Patterns: forms/date-input for the
// date, navigation/navigation-menu (segmented) for reported or restated,
// data-display/table for the report and the closes, content-management/modal
// for a close.

interface Report { engine: { id: string; version: string }; title: string; columns: Array<{ key: string; label: string; kind: string }>; rows: Array<Record<string, unknown>>; point: { as_of: string | null; known_seq: string | null; read_to_seq: string } }
interface Period { id: string; period: string; starts_at: string; ends_at: string; closed_seq: string; created_at: string; adjustments: number }
interface Adjustment { seq: string; type: string; tbl: string | null; row_id: string | null; actor: string; recorded_at: string; effective_at: string; proposal_id: string | null }

const endOfDay = (d: string) => new Date(`${d}T23:59:59.999Z`).toISOString();
const lastMonth = () => { const n = new Date(); return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth() - 1, 1)).toISOString().slice(0, 7); };

export function Ledger() {
  const { me, refreshStats } = useSession();
  usePageTitle('Point in time');
  const periods = useApi<{ folded_seq: string; periods: Period[] }>('/v1/ledger/periods');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [view, setView] = useState<'restated' | 'reported'>('restated');
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [adjFor, setAdjFor] = useState<string | null>(null);
  const toast = useToast();
  // As reported: what the system knew when the month holding the date closed.
  const closeFor = periods.data?.periods.find((p) => date >= p.starts_at.slice(0, 10) && date < p.ends_at.slice(0, 10));

  async function run() {
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = { params: {}, as_of: endOfDay(date) };
      if (view === 'reported' && closeFor) body.known_seq = closeFor.closed_seq;
      setReport(await api<Report>('/v1/reports/pipeline_by_stage/run', { body }));
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Point in time</h1><p>Every change is on the ledger with when it was entered and when it took effect, so a report can show any past date: as it was known then, or as it is known now.</p></div>
        {isAdmin(me) && <Button type="submit" variant="secondary" onClick={() => setClosing(true)}>Close a month</Button>}
      </div>
      <section className={cardClass} aria-labelledby="asof-h">
        <div className="card-head"><h2 id="asof-h">Pipeline by stage, as of a date</h2></div>
        <form className="card-pad stack" onSubmit={(e) => { e.preventDefault(); void run(); }}>
          <ErrorBanner error={error} />
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 12 }}>
            <Field label="As of"><input className={fieldClass} type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} /></Field>
            <ToggleGroup label="Which figures" value={view} onChange={setView} options={[['restated', 'As known now'], ['reported', 'As reported']]} />
            <Button type="submit" disabled={busy || !date}>Show</Button>
          </div>
          {view === 'reported' && <p className="small muted" style={{ margin: 0 }}>{closeFor ? `As reported when ${closeFor.period} closed (seq ${closeFor.closed_seq}).` : 'That month is not closed, so as reported and as known now are the same.'}</p>}
        </form>
        {report && (
          <div aria-live="polite">
            <Table label={report.title}>
                <caption className="sr-only">{report.title} as of {date}</caption>
                <thead><tr>{report.columns.map((c) => <th key={c.key} scope="col">{c.label}</th>)}</tr></thead>
                <tbody>{report.rows.map((r, i) => <tr key={i}>{report.columns.map((c) => <td key={c.key}>{formatCell(c.kind, r[c.key], (r.currency as string) ?? 'USD')}</td>)}</tr>)}</tbody>
              </Table>
            <p className="faint small card-pad" style={{ margin: 0 }}>Read to ledger seq {report.point.read_to_seq}{report.point.known_seq ? `, as known at seq ${report.point.known_seq}` : ''} · {report.engine.id} v{report.engine.version}. The same seq gives the same numbers.</p>
          </div>
        )}
      </section>
      <section className={cardClass} aria-labelledby="closes-h">
        <div className="card-head"><h2 id="closes-h">Closed months</h2>{periods.data && <span className="faint small">Ledger read to seq {periods.data.folded_seq}</span>}</div>
        <ErrorBanner error={periods.error} />
        {periods.loading && !periods.data ? <ListSkeleton rows={2} label="Loading closes" /> : !periods.data?.periods.length ? <Empty title="No month is closed">Close a month to fix what it reported. Later changes that take effect inside it show as adjustments.</Empty> : (
          <Table label="Closed months">
              <caption className="sr-only">Closed months and their adjustments</caption>
              <thead><tr><th scope="col">Month</th><th scope="col">Closed at</th><th scope="col">Adjustments</th></tr></thead>
              <tbody>{periods.data.periods.map((p) => (
                <tr key={p.id}>
                  <td>{p.period}</td>
                  <td className="small">seq {p.closed_seq} · <Time iso={p.created_at} /></td>
                  <td>{p.adjustments ? <Button type="submit" variant="ghost" size="sm" onClick={() => setAdjFor(adjFor === p.period ? null : p.period)} aria-expanded={adjFor === p.period}>{p.adjustments} since the close</Button> : <span className="faint">None</span>}</td>
                </tr>
              ))}</tbody>
            </Table>
        )}
        {adjFor && <Adjustments period={adjFor} />}
      </section>
      {closing && <CloseMonth onClose={() => setClosing(false)} onDone={(out) => { toast(outcomeMessage(out, 'Closed'), outcomeTone(out)); refreshStats(); setClosing(false); void periods.reload(); }} />}
    </div>
  );
}

function Adjustments({ period }: { period: string }) {
  const adj = useApi<Adjustment[]>(`/v1/ledger/periods/${period}/adjustments`, [period]);
  return (
    <div className="card-pad" aria-live="polite">
      <h3 className="small" style={{ margin: '0 0 6px' }}>Entered after {period} closed, effective inside it</h3>
      <ol className="timeline">
        {adj.data?.map((a) => (
          <li key={a.seq} className="warn">{a.type.replace(/^row\.(gl_)?/, '').replace(/[._]/g, ' ')} {a.row_id ? <span className="faint">({a.row_id.slice(0, 8)})</span> : null}
            <div className="faint small">effective <Time iso={a.effective_at} />, entered <Time iso={a.recorded_at} />{a.proposal_id && <> · <Link to={`/activity/${a.proposal_id}`}>Proposal</Link></>}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function CloseMonth({ onClose, onDone }: { onClose: () => void; onDone: (o: Outcome) => void }) {
  const [period, setPeriod] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function save() {
    setBusy(true); setError(null);
    try { onDone(await api<Outcome>('/v1/ledger/periods', { body: { period, reason: `Close ${period}` } })); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <ConfirmDialog open title="Close a month" confirmLabel="Propose the close" busy={busy} onConfirm={() => void save()} onCancel={onClose}>
      <ErrorBanner error={error} />
      <p className="muted small" style={{ margin: 0 }}>A close is a change set a person signs. Once closed, a month is never reopened; later changes inside it show as adjustments.</p>
      <Field label="Month" required><input className={fieldClass} type="month" value={period} max={lastMonth()} onChange={(e) => setPeriod(e.target.value)} /></Field>
    </ConfirmDialog>
  );
}
